import type { SavedView, UserViewsResponse } from './saved-view'

export interface ApiColumnDefinition {
  reportTypeId: number
  columnName: string
  columnNameEnglish: string
  columnTypeCode: string
  visible: boolean
  visibleToInternalUser: boolean
  visibleToExternalUser: boolean
  id: string
}

export interface Workflow {
  id: string
  name: string
  description: string
  createdAt: string
  updatedAt: string
  columns: FieldConfiguration[]
  data: WorkflowData[]
}

export type ColumnType = "text" | "number" | "select" | "date"

export interface ColumnOption {
  id: string
  label: string
  value: string
}

export interface WorkflowData {
  id: string
  [key: string]: any
}

// New WorkflowItem interface matching the real API response
export interface WorkflowItem {
  // Core identification
  id?: string // We'll generate this from atsPlacementId or vmsId
  workflowHubId?: string
  atsPlacementId?: string
  atsSourceId?: number
  vmsId?: string

  // Worker information
  workerEmail?: string
  workerPhone?: string
  workerLocation?: string
  workerProfileStatus?: string

  // Computed worker name for backward compatibility
  workerFullName?: string // Will be computed from first + last name

  // Assignment and management
  assignedToUserEmail?: string
  assignedToUserFullName?: string
  managerFullName?: string
  managerEmail?: string
  recruiterFullName?: string
  primaryJobOwnerFullName?: string

  // Company and client information
  clientName?: string
  supplierName?: string
  internalCompany?: string
  branch?: string

  // Job details
  jobTitle?: string
  profileType?: string
  workOrderStatus?: string

  // Legal and compliance
  eligibilityToWork?: string
  permitExpirationDate?: string

  // Financial information
  primaryPayRate?: number
  primaryBillRate?: number
  primaryBillRateUnitCode?: string
  otEligible?: boolean
  otPayRate?: number
  otBillRate?: number

  // Dates
  startDate?: string
  endDate?: string
  onboardingPackageSentDate?: string
  onboardingPackageCompleteDate?: string

  // Additional fields
  duration?: number
  timesheetMethod?: string
  healthStatus?: string
  
  // Collections
  Alerts?: AlertDto[]

  // System fields
  isDeleted?: boolean
  createdByProfileId?: number
  createdDateTime?: string
  lastModifiedByProfileId?: number
  lastModifiedDateTime?: string
  
  // Assignment projection fields
  hasMultipleWorkOrders?: boolean

  // Computed fields for UI compatibility
  stage?: WorkflowStage // Will be computed from workOrderStatus
  client?: string // Alias for clientName
  recruiter?: string // Alias for recruiterFullName
  assignedTo?: string // Alias for assignedToUserFullName

  // Allow for additional dynamic fields
  [key: string]: any
}

// Keep WorkOrder as an alias for backward compatibility during transition
export type WorkOrder = WorkflowItem

export type WorkflowStage = "onboarding" | "active" | "extension" | "offboarding" | "complete"

export interface User {
  id: string
  name: string
  count: number
  role?: string
  email?: string
  avatar?: string
}

// New interface for assigned users from the API
export interface AssignedUser {
  email: string
  fullName: string
}

// Interface for authorized user information in private columns
export interface UserReportDto {
  profileId: string
  fullName?: string
  email: string
}

// Interface for user search API response
export interface UserSearchApiResponse {
  EntityId: string
  FirstName: string
  LastName: string
  PrimaryEmail: string
}

// Response wrapper for user search results
export interface UserSearchResponse {
  '@odata.count': number
  value: UserSearchApiResponse[]
}

export interface Filters {
  [key: string]: string[] | undefined
}

// API request interface for the workflow search
export interface WorkflowSearchRequest {
  select: string
  filter: string | null
  orderBy: string | null
  top: number
  skip: number
  search: string
  facets: string[] | null
  count?: boolean
  suggesterName?: string
}

// API response interface for paginated results
export interface WorkflowApiResponse {
  data: WorkflowItem[]
  totalCount: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  facets?: Record<string, { value: string; count: number }[]>
}

// API response interface for suggestions
export interface SuggestionsApiResponse {
  value?: string[]
}

// Add new types for improved state management
export interface WorkflowState {
  // UI State
  currentStage?: WorkflowStage
  isFilterDrawerOpen: boolean
  isBulkEditModalOpen: boolean
  currentBulkEditField: string | null
  
  // Data State
  workflowItems: WorkflowItem[] // Renamed from workOrders
  // Optimistic overlay: rowId -> field -> { expected, prior, ts }. Pending values are
  // re-applied on top of freshly-fetched rows (in SET_WORK_ORDERS) until the server
  // confirms the change (fetched value matches expected) or a safety timeout elapses,
  // so an optimistic edit doesn't flicker back while the search index catches up.
  pendingOptimistic: Record<string, Record<string, { expected: any; prior: any; ts: number }>>
  /**
   * Rows optimistically removed (id → removal timestamp). SET_WORK_ORDERS
   * filters these ids out of fetched results until the suppression window
   * expires, so a silent refetch racing Azure Search index propagation can't
   * resurrect a row the user just deleted. Registered only after the delete
   * API succeeded, so no failure-revert path is needed.
   */
  pendingRemovals: Record<string, number>
  columns: FieldConfiguration[]
  visibleColumns: FieldConfiguration[]
  filters: Filters
  selectedItems: string[]
  
  // Pagination State
  currentPage: number
  pageSize: number
  itemsPerPage: number | null // null = auto, number = fixed page size
  totalCount: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  
  // Loading State - consolidated into a single enum
  loadingState: LoadingState
  
  // Error State
  error: string | null
  
  // Views State
  savedViews: SavedView[]
  currentView: SavedView | null
  hasUnsavedChanges: boolean
  
  // View data from API
  viewsResponse: UserViewsResponse | null
  
  // User State
  currentUser: User

  // Configuration State
  configuration?: WorkflowConfiguration
  isLoadingConfiguration?: boolean
  configurationError?: string | null

  // Stages
  stages: StageConfiguration[]

  // Assigned Users State
  assignedUsers: AssignedUser[]
  isLoadingAssignedUsers: boolean
  assignedUsersError: string | null

  // Facets
  facets?: Record<string, { value: string; count: number }[]>

  // Stage tab counts (cross-stage, keyed by lowercase stageCode)
  stageCounts?: Record<string, number>

  // Filter State
  stageFilters: { stageId: string; fieldConfigurationId: string; filter: string; values?: any }[]
  globalFilters: { fieldConfigurationId: string, filter: string, values?: string }[]
  temporaryQueryFilter?: string | null
  /**
   * Optional human-readable label for the temporary query filter. Surfaced
   * in the dismissible chip above the grid so users can see why the row
   * set is narrowed and clear it explicitly (rather than having to guess
   * what "temporary filter" means).
   */
  temporaryQueryFilterLabel?: string | null
  /**
   * Whether this temporary filter should also PAUSE the stage's saved column
   * filters (see applicableStageFilters in workflow-grid). Request tabs pause
   * unconditionally; WorkOrder stages don't, because the assignment-search
   * flow sets its stage filters AS the scope in the same gesture. A `?worker=`
   * deep link is the exception on WorkOrder: its scope has nothing to do with
   * whatever the user last filtered that stage by, so an unrelated saved
   * filter would AND with it and land the link on an empty grid.
   */
  temporaryQueryFilterPausesStageFilters?: boolean

  // New added workOrdersFacets
  workOrdersFacets?: Record<string, { value: string; count: number }[]>;

  // Code Values State
  codeValuesData?: Record<string, any[]>;
  isLoadingCodeValues?: boolean;
  codeValuesError?: string | null;

  // Column Widths State
  columnWidths?: Record<string, number>;
  /** Message notifications preference, hydrated from UserViewState. Defaults to true. */
  messageNotificationsEnabled?: boolean;
  
  // Pinned (sticky) columns per stage (stageId -> fieldConfigurationId)
  pinnedColumnsByStage?: Record<string, string | null>;

  // Sort state per stage (stageId -> { columnCode, direction })
  sortByStage: Record<string, { columnCode: string; direction: 'asc' | 'desc' } | null>;
  
  // Show-completed toggle state per stage (for fixed-filter stages)
  showCompletedByStage: Record<string, boolean>;

  // Client Custom Fields State
  originalConfiguration?: WorkflowConfiguration;
  activeClientFboId?: number | null;
  activeClientName?: string | null;
  cachedClientCustomFields: Map<number, ClientCustomField[]>;
  customFieldsApplied: boolean;
  isHandlingClientFilterChange: boolean;
}

export enum LoadingState {
  IDLE = 'idle',
  LOADING_COLUMNS = 'loading_columns',
  LOADING_WORK_ORDERS = 'loading_work_orders',
  LOADING_ROWS = 'loading_rows',
  UPDATING_WORK_ORDER = 'updating_work_order',
  BULK_UPDATING = 'bulk_updating',
  ERROR = 'error'
}

export type WorkflowAction =
  | { type: 'SET_CURRENT_STAGE'; payload: WorkflowStage }
  | { type: 'SET_LOADING_STATE'; payload: LoadingState }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'TOGGLE_FILTER_DRAWER' }
  | { type: 'SET_WORK_ORDERS'; payload: { data: WorkflowItem[]; totalCount: number; hasNextPage: boolean; hasPreviousPage: boolean; facets?: Record<string, { value: string; count: number }[]> } }
  | { type: 'SET_COLUMNS'; payload: { columns: FieldConfiguration[]; visibleColumns: FieldConfiguration[] } }
  | { type: 'UPDATE_VISIBLE_COLUMNS'; payload: FieldConfiguration[] }
  | { type: 'UPDATE_FILTER'; payload: { key: string; value: any } }
  | { type: 'CLEAR_FILTERS' }
  | { type: 'SET_SELECTED_ITEMS'; payload: string[] }
  | { type: 'TOGGLE_ITEM_SELECTION'; payload: string }
  | { type: 'SELECT_ALL_ITEMS'; payload: string[] }
  | { type: 'DESELECT_ALL_ITEMS' }
  | { type: 'SET_CURRENT_PAGE'; payload: number }
  | { type: 'SET_PAGE_SIZE'; payload: number }
  | { type: 'SET_ITEMS_PER_PAGE'; payload: number | null }
  | { type: 'UPDATE_WORK_ORDER_FIELD'; payload: { orderId: string; field: string; value: any } }
  | { type: 'APPLY_OPTIMISTIC_UPDATES'; payload: { updates: { id: string; updates: Record<string, any> }[] } }
  | { type: 'REVERT_OPTIMISTIC'; payload: { id: string; fields: string[] } }
  | { type: 'REMOVE_WORK_ORDER'; payload: string }
  | { type: 'OPEN_BULK_EDIT_MODAL'; payload: string }
  | { type: 'CLOSE_BULK_EDIT_MODAL' }
  | { type: 'SET_CURRENT_VIEW'; payload: SavedView | null }
  | { type: 'SET_SAVED_VIEWS'; payload: SavedView[] }
  | { type: 'SET_UNSAVED_CHANGES'; payload: boolean }
  | { type: 'SET_VIEWS_RESPONSE'; payload: UserViewsResponse }
  | { type: 'ADD_CUSTOM_COLUMN'; payload: FieldConfiguration }
  | { type: 'DELETE_COLUMN'; payload: string }
  | { type: 'SET_CONFIGURATION'; payload: WorkflowConfiguration }
  | { type: 'SET_LOADING_CONFIGURATION'; payload: boolean }
  | { type: 'SET_CONFIGURATION_ERROR'; payload: string | null }
  | { type: 'SET_ASSIGNED_USERS'; payload: AssignedUser[] }
  | { type: 'SET_LOADING_ASSIGNED_USERS'; payload: boolean }
  | { type: 'SET_ASSIGNED_USERS_ERROR'; payload: string | null }
  | { type: 'SET_STAGE_FILTERS'; payload: { stageId: string; fieldConfigurationId: string; filter: string; values?: any }[] }
  | { type: 'SET_GLOBAL_FILTERS'; payload: { fieldConfigurationId: string, filter: string }[] }
  | { type: 'CLEAR_ALL_FILTERS' }
  | { type: 'SET_FACETS'; payload: Record<string, { value: string; count: number }[]> | undefined }
  | { type: 'SET_CODE_VALUES_DATA'; payload: Record<string, any[]> }
  | { type: 'SET_LOADING_CODE_VALUES'; payload: boolean }
  | { type: 'SET_CODE_VALUES_ERROR'; payload: string | null }
  | { type: 'SET_COLUMN_WIDTHS'; payload: Record<string, number> }
  | { type: 'SET_MESSAGE_NOTIFICATIONS'; payload: boolean }
  | { type: 'SET_PINNED_COLUMN'; payload: { stageId: string; columnId: string | null } }
  | { type: 'SET_PINNED_COLUMNS_BY_STAGE'; payload: Record<string, string | null> }
  | { type: 'APPLY_CUSTOM_FIELDS'; payload: { customFields: ClientCustomField[]; clientFboId: number; clientName: string } }
  | { type: 'CLEAR_CUSTOM_FIELDS' }
  | { type: 'CACHE_CLIENT_CUSTOM_FIELDS'; payload: { clientFboId: number; customFields: ClientCustomField[] } }
  | { type: 'SET_HANDLING_CLIENT_FILTER_CHANGE'; payload: boolean }
  | { type: 'APPLY_ASSIGNMENT_SEARCH'; payload: { stage: string; assignmentId: number; stageFilters: any[] } }
  | { type: 'APPLY_TEMPORARY_QUERY_FILTER'; payload: { filter: string; label?: string; pauseStageFilters?: boolean } }
  | { type: 'CLEAR_TEMPORARY_FILTER' }
  | { type: 'SET_STAGE_COUNTS'; payload: Record<string, number> }  
  | { type: 'SET_SHOW_COMPLETED'; payload: { stageId: string; showCompleted: boolean } }
  | { type: 'SET_SORT_STATE'; payload: { stageId: string; sort: { columnCode: string; direction: 'asc' | 'desc' } | null } }


// Add WorkflowConfiguration and related types
export interface WorkflowConfiguration {
  stageConfigurations: StageConfiguration[];
  customFieldsMetadata: CustomFieldMetadata[];
  fieldsInUse: FieldConfiguration[];
  externalSourceConfigurations?: ExternalSourceConfiguration[];
  actionConfigurations?: ActionConfiguration[];
  authorizedActions?: AuthorizedAction[];
  globalFilterIds: string[];
  alwaysFetchedFieldCodes?: string[];
  // New fields for saved filters and view settings
  savedGlobalFilters?: { fieldConfigurationId: string; filter: string; values?: string }[];
  savedStageFilters?: { stageId: string; fieldConfigurationId: string; filter: string; values?: string }[];
  viewSettingsId?: string;
  itemsPerPage?: number | null; // null = auto, number = fixed page size
      columnWidths?: string; // Serialized JSON string for saved column widths
  /** Message notifications preference. Undefined/null means never set, which reads as on. */
  messageNotificationsEnabled?: boolean | null;
  showPendingStatusQuickFilter?: boolean;
  maintenanceMessage?: MaintenanceMessage;
}

// Reusable maintenance-window banner, authored on the Default WorkflowHubConfiguration
// doc and served via GET /configurations. Visibility is gated client-side on `isEnabled`
// plus the optional Start/End window; `{time}` in the message is replaced with `endDateTime`.
export interface MaintenanceMessage {
  isEnabled: boolean;
  type?: 'Info' | 'Warning' | 'Error' | string;
  messageEnglish?: string;
  messageFrench?: string;
  // Optional override for the "upcoming" notice body; falls back to the default template.
  upcomingMessageEnglish?: string;
  upcomingMessageFrench?: string;
  startDateTime?: string | null;
  endDateTime?: string | null;
  // Hours before startDateTime that the "upcoming" notice begins showing (default 24).
  notifyHoursBefore?: number | null;
}

export interface StageConfiguration {
  stageId: string;
  stageCode: string;
  titleEnglish: string;
  titleFrench: string;
  fieldConfigurations: FieldConfiguration[];
  order: number;
  pinnedColumnId?: string | null;
  sortColumn?: string | null;
  sortDirection?: 'asc' | 'desc' | null;
  isInlineEditsEnabled?: boolean;
  fixedFilter?: string | null;
  excludeGlobalFilters?: boolean;
  showCompletedToggle?: boolean;
  showCompleted?: boolean;
}

export interface FieldConfiguration {
  fieldConfigurationId: string;
  columnCode: string;
  columnTypeCode: string;
  columnNameEnglish: string;
  columnNameFrench: string;
  color: string;
  isEditable: boolean;
  customValuesSource: string;
  customValuesType: string;
  customValues: CustomValue[];
  order: number;
  isCustomField?: boolean;
  isFacetable?: boolean;
  isFilterable?: boolean;
  isSearchable?: boolean;
  isSortable?: boolean;
  allowMultipleSelections?: boolean;
  subColumnName?: string;
  codeValueSource?: string;
  fieldFormatting?: string; // Regex pattern for field validation
  isStaticField?: boolean; // New field to indicate if this is a static field that should be excluded from search requests
  isPrivate?: boolean; // Indicates if this field is private and restricted to specific users
  authorizedUserList?: UserReportDto[]; // List of users authorized to view this private field
  note?: string; // HTML/styled text note for the field
  displayNote?: boolean; // Whether to display the note info icon in the grid
  mappingSource?: string; // Source of field mapping (e.g., "FboCustomField")
  mappingSourceTargetColumn?: string; // Target column name in mapping source (e.g., "Dropdown_9")
  isRetainDataEnabled?: boolean; // Whether to retain column data for newly created line items with the same Assignment ID
  isEnabledForClients?: boolean;
  badgeColumnCode?: string;
  // Tenant attribution from the BE. "Default" means the field is sourced from the
  // shared cross-tenant Default partition — the field-edit panel uses this to flip
  // into read-only mode so a tenant user can't accidentally edit the shared schema
  // (which would silently affect every tenant). Tenant-scoped fields carry the
  // tenant name (e.g. "Procom"); cross-tenant defaults carry "Default".
  tenantName?: string;
}

export interface CustomValue {
  code: string;
  titleEnglish: string;
  titleFrench: string;
  color: string;
  isDeleted: boolean;
}

export interface AlertDto {
  Code: string;
  TitleEnglish?: string;
  TitleFrench?: string;
  Color?: string;
  Icon?: string;
  Type: "Error" | "Warning" | "Information";
  IsDeleted: boolean;
}

export interface CustomFieldMetadata {
  inputDataType: string;
  fieldConfigurationIds: string[];
}

// Some of these are not used in the UI at the moment, but will be in the future
export const CustomFieldInputTypeStruct = {
  Text: "Text",
  YesNo: "YesNo",
  Dropdown: "Dropdown",
  Date: "Date",
  Number: "Number",
  // Files: "Files",
  // Link: "Link",
  // LastUpdated: "LastUpdated",
  // OBLink: "ObLink",
  // FBOWorkerLink: "FboWorkerLink",
  Email: "Email",
  Checklist: "Checklist",
} as const;

export const ColumnTypeStruct = {
  Number: "Number",
  Text: "Text",
  Email: "Email",
  TextList: "TextList",
  Boolean: "Boolean",
  Decimal: "Decimal",
  Double: "Double",
  Date: "DateTime",
  Money: "Money",
  Checklist: "Checklist",
} as const;

// --- Checklist Types ---

export interface ChecklistItem {
  title: string;
  isCompleted: boolean;
  order: number;
  isDeleted: boolean;
}

export interface ChecklistData {
  items: ChecklistItem[];
  totalItems: number;
  completedItems: number;
}

// --- External Source and Action Types ---

export interface ExternalSourceConfiguration {
  titleEnglish: string;
  titleFrench: string;
  link: string;
  condition?: string;
}

export interface ActionConfiguration {
  actionCode: string;
  titleEnglish: string;
  titleFrench: string;
  icon?: string; // Icon name: 'check', 'external-link', 'file-text', 'clock', 'arrow-right', 'x-circle', 'grip', 'mail', 'history', 'refresh', 'trash', 'eye', 'ban'
  isDefault?: boolean; // Default actions appear below separator
  condition?: string; // Can include Stage checks like "Stage eq 'Active'"
  // open_email_preview is VMS-specific — opens a read-only slide-out with the
  // decrypted email body and attachment metadata. Resolves the email id from
  // item.linkedEmailId (VmsRequest rows) or item.id (VmsEmail rows).
  actionType: 'history' | 'external_link' | 'stage_transition' | 'custom_modal' | 'open_email_preview';
  
  // For external_link
  link?: string;
  
  // For stage_transition
  targetStage?: string;
  requiresConfirmation?: boolean;
  allowNotes?: boolean;
  allowBulk?: boolean;

  // When set, this action is a cell link applied to the specified column instead of an action menu item
  columnCode?: string;
}

export interface AuthorizedAction {
  actionCode: string;
  functionalRoleIds: number[];
  userProfileTypeIds: number[];
}

export const WorkflowHubActionCodeStruct = {
  DeleteField: "DeleteField",
  UpdateField: "UpdateField",
  CreateField: "CreateField",
} as const;

export const ExternalSourceCodeStruct = {
  Fbo: "FBO",
  Ob: "OB",
} as const;

// Client custom fields types
export interface ClientCustomField {
  DisplayName: string;
  FieldName: string;
  FieldType: string;
  SelectValues: string | null;
}

export interface ClientCustomFieldsResponse {
  ClientId: number;
  EntityTypeId: number;
  CustomFields: ClientCustomField[];
}

export enum FboEntityType {
  WorkOrder = 17
}

export type OBRequestStatus = 'Draft' | 'InProgress' | 'Completed' | 'ReadOnlyTemplate'
export type OBRequestType = 'Onboarding' | 'Extension' | 'ChangeRequest' | 'DocumentRequest'
export type OBTaskStatus = 'New' | 'InProgress' | 'RequiresApproval' | 'RequiresReview' | 'Completed' | 'Returned' | 'Declined' | 'Discarded'
export type OBActorType =
  | 'Consultant'
  | 'ClientService'
  | 'AccountManager'
  | 'Executive'
  | 'Recruiter'
  | 'SubvendorOrg'
  | 'SubvendorWorker'
  | 'ReadOnlyPreview'

export interface OBTaskActor {
  actorType: OBActorType
  name: string
  email: string
  status: OBTaskStatus
  completedAt: string | null
}

export interface OBRequestSummary {
  id: string
  requestType: OBRequestType
  status: OBRequestStatus
  workOrderNumber?: string
  assignmentId?: number
  createdDateTime?: string
  lastModifiedDateTime?: string
}

export interface OBRequestTask {
  id: string
  requestId: string
  taskName?: string
  taskStatus: OBTaskStatus
  taskType?: string
  order?: number
  startDateTime?: string | null
  completedAt?: string | null
  actors?: OBTaskActor[]
}

export interface OBRequestGroup {
  request: OBRequestSummary
  tasks: OBRequestTask[]
}
